import { Badge } from "@dropbox/dig-components/badge";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AppsFill,
  AppsLine,
  SyncingLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import useConnectors from "@mirage/service-connectors/useConnectors";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useRef } from "react";
import styles from "./GlobalNav.module.css";
import { GlobalNavAppsIconTooltip } from "./GlobalNavAppsIconTooltip";
import { GlobalNavLink } from "./GlobalNavLink";
import { useAppsTooltip } from "./useAppsTooltip";

export function GlobalNavAppsIcon({
  appsLink,
  isExpanded,
  onAppsClick,
  showTooltipForNavLink,
  useMobileNav = false,
}: {
  appsLink: string;
  isExpanded: boolean;
  onAppsClick: React.MouseEventHandler;
  showTooltipForNavLink: boolean;
  useMobileNav?: boolean;
}) {
  const { hasExpiredConnections, hasReauthConnections } = useConnectors();
  const { initialSync } = useAppsTooltip();
  const hasWarnings = hasExpiredConnections || hasReauthConnections;
  const showingSyncingState = !hasWarnings && initialSync;

  const navLinkRef = useRef(null);
  let collapsedBadge = undefined;
  let expandedBadge = undefined;

  if (hasWarnings) {
    collapsedBadge = (
      <Badge
        variant="alert"
        className={classnames(
          styles.globalNavBadge,
          isExpanded && styles.hidden,
        )}
      >
        {"!"}
      </Badge>
    );

    expandedBadge = (
      <Badge
        variant="alert"
        className={classnames(
          styles.globalNavBadge,
          styles.expanded,
          !isExpanded && styles.hidden,
        )}
      >
        {"!"}
      </Badge>
    );
  } else if (initialSync) {
    collapsedBadge = (
      <Badge
        variant="attention"
        className={classnames(
          styles.globalNavBadge,
          isExpanded && styles.hidden,
        )}
      >
        <UIIcon size="small" src={SyncingLine} />
      </Badge>
    );

    expandedBadge = (
      <Badge
        variant="attention"
        className={classnames(
          styles.globalNavBadge,
          styles.expanded,
          !isExpanded && styles.hidden,
        )}
      >
        <UIIcon size="small" src={SyncingLine} />
      </Badge>
    );
  }

  return (
    <>
      <GlobalNavLink
        hasNotificationBadge={hasWarnings || initialSync}
        iconBadge={collapsedBadge}
        textBadge={expandedBadge}
        label={showingSyncingState ? i18n.t("apps_syncing") : i18n.t("apps")}
        tooltip={
          showingSyncingState
            ? i18n.t("apps_initial_sync_tooltip")
            : i18n.t("apps")
        }
        to={appsLink}
        lineIcon={AppsLine}
        fillIcon={AppsFill}
        navTab="apps"
        onClick={onAppsClick}
        showTooltip={showTooltipForNavLink || (!useMobileNav && initialSync)}
        ref={navLinkRef}
      />
      <GlobalNavAppsIconTooltip triggerRef={navLinkRef} />
    </>
  );
}
